import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { api } from '../utils/api';

const POLL_INTERVAL_MS = 90 * 1000;

export function useSellerNotifications(isVendor) {
  const { getToken } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    if (!getToken || !isVendor) {
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    setLoading(true);
    try {
      const res = await api('/api/notifications', { getToken });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setNotifications(data.notifications || []);
        setUnreadCount(data.unreadCount ?? 0);
      }
    } catch {
    } finally {
      setLoading(false);
    }
  }, [getToken, isVendor]);

  useEffect(() => {
    if (!isVendor) return;
    fetchNotifications();
  }, [isVendor, fetchNotifications]);

  useEffect(() => {
    if (!isVendor || typeof document === 'undefined') return;
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchNotifications();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible') fetchNotifications();
    }, POLL_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      clearInterval(interval);
    };
  }, [isVendor, fetchNotifications]);

  const markAsRead = useCallback(async (id) => {
    if (!getToken) return;
    try {
      const res = await api(`/api/notifications/${id}/read`, { getToken, method: 'PATCH' });
      if (res.ok) {
        setNotifications((prev) =>
          prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n))
        );
        setUnreadCount((c) => Math.max(0, c - 1));
      }
    } catch {}
  }, [getToken]);

  const markAllAsRead = useCallback(async () => {
    if (!getToken) return;
    try {
      const res = await api('/api/notifications/read-all', { getToken, method: 'PATCH' });
      if (res.ok) {
        setNotifications((prev) =>
          prev.map((n) => ({ ...n, readAt: n.readAt || new Date().toISOString() }))
        );
        setUnreadCount(0);
      }
    } catch {}
  }, [getToken]);

  return {
    notifications,
    unreadCount,
    loading,
    markAsRead,
    markAllAsRead,
    refetch: fetchNotifications,
  };
}
